import React, { useState, useEffect } from 'react';
import './JsonGrades.css';
import { jsonGradesToText, getWordCountWarning } from '../utils/gradeFormatters';

/**
 * Component for rendering JSON-structured grades
 * Handles both display and editing of grade data
 */
function JsonGrades({ 
  jsonData, 
  category, 
  wordCount, 
  userData, 
  isEditingGrade, 
  editedGradeText, 
  setEditedGradeText, 
  handleEditGrade, 
  handleSaveGrade, 
  handleCancelGradeEdit, 
  gradeSaveStatus, 
  editedGrades, 
  setEditedGrades, 
  isStructuredEdit, 
  setIsStructuredEdit,
  taskId, 
  authToken, 
  API_BASE_URL, 
  localJsonData, 
  setLocalJsonData, 
  setDisplayedGradeText,
  setGradeSaveStatus, 
  setIsEditingGrade,
  toggleFAQ
}) {
  
  const currentJsonData = localJsonData || jsonData;
  const gradesRaw = currentJsonData.grades || {};
  
  // Normalize grade keys to lowercase for consistent access
  const grades = {};
  Object.keys(gradesRaw).forEach(key => {
    const value = gradesRaw[key];
    const norm = key.toLowerCase().replace(/\s+/g, '_');
    if (norm === 'language_use') {
      grades.language = value;
    } else if (norm === 'accuracy_of_mechanics') {
      grades.mechanics = value;
    } else if (norm === 'language') {
      grades.language = value;
    } else if (norm === 'mechanics') {
      grades.mechanics = value;
    } else {
      grades[key.toLowerCase()] = value;
    }
  });
  
  // For VCE Section A/C, ensure all expected grade components exist
  React.useEffect(() => {
    if ((category === 'VCE_English_SectionA' || category === 'VCE_English_SectionC') && grades) {
      const expectedComponents = ['knowledge', 'coherence', 'evidence', 'language'];
      const missingComponents = expectedComponents.filter(comp => !grades[comp]);
      
      if (missingComponents.length > 0) {
        console.warn(`Missing grade components for ${category}:`, missingComponents);
        console.log('Current grades object:', grades);
        console.log('Original grades object:', gradesRaw);
        console.log('Full JSON data structure:', currentJsonData);
      }
    }
  }, [category, grades, gradesRaw, currentJsonData]);
  
  // Debug logging for VCE Section A
  React.useEffect(() => {
    if (category === 'VCE_English_SectionA' || category === 'VCE_English_SectionC') {
      console.log('VCE Section A/C Grade Data Debug:', {
        category,
        normalizedGrades: grades,
        originalGrades: gradesRaw,
        hasKnowledge: !!grades.knowledge,
        hasCoherence: !!grades.coherence,
        hasEvidence: !!grades.evidence,
        hasLanguage: !!grades.language,
        normalizedKeys: Object.keys(grades),
        originalKeys: Object.keys(gradesRaw),
        fullJsonData: currentJsonData
      });
    }
  }, [category, grades, gradesRaw, currentJsonData]);

  // Track if we've already fetched the saved grade
  const [hasFetchedGrade, setHasFetchedGrade] = React.useState(false);

  // Check if there's a saved grade update on mount
  React.useEffect(() => {
    const fetchSavedGrade = async () => {
      if (!taskId || !authToken || hasFetchedGrade) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/results/${taskId}/grade`, {
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.has_teacher_grade && data.json_grades) {
            // There's a teacher-edited grade in JSON format
            console.log('Found teacher-edited JSON grades, updating display');
            
            // Update the local JSON data with the saved grades
            const updatedData = localJsonData ? { ...localJsonData } : { ...jsonData };
            updatedData.grades = data.json_grades;
            updatedData.custom_grade_text = data.grade_text; // May be null
            setLocalJsonData(updatedData);
            setHasFetchedGrade(true);
          } else if (data.grade_text && data.grade_text.includes('[TEACHER]')) {
            // Legacy text-based teacher grade
            console.log('Found teacher-edited text grade, updating display');
            
            if (localJsonData) {
              const updatedData = { ...localJsonData };
              updatedData.custom_grade_text = data.grade_text;
              setLocalJsonData(updatedData);
            } else {
              const newData = { ...jsonData };
              newData.custom_grade_text = data.grade_text;
              setLocalJsonData(newData);
            }
            setHasFetchedGrade(true);
          }
        }
        // Mark as fetched even if no grade found
        setHasFetchedGrade(true);
      } catch (error) {
        console.log('Could not fetch saved grade:', error.message);
        // Mark as fetched even on error to prevent retry loop
        setHasFetchedGrade(true);
      }
    };
    
    fetchSavedGrade();
  }, [taskId, authToken, hasFetchedGrade]); // Remove jsonData and localJsonData from dependencies

  // Reset the fetch flag when taskId changes
  React.useEffect(() => {
    setHasFetchedGrade(false);
  }, [taskId]);

  // Helper function to format grade text with proper styling
  const formatGradeDisplay = (gradeData) => {
    if (!gradeData) return '';
    
    const { score, max, feedback, percentage } = gradeData;
    let display = `${score}/${max}`;
    if (percentage !== undefined) {
      display += ` (${percentage}%)`;
    }
    
    if (feedback) {
      display += `\n${feedback}`;
    }
    
    return display;
  };

  // Handle structured grade editing
  const handleStructuredEdit = () => {
    // If there's custom grade text, we need to parse it back to structured format
    if (currentJsonData.custom_grade_text) {
      // Extract grades from the custom text
      const text = currentJsonData.custom_grade_text;
      const extractedGrades = {};
      
      // Check if this is Australian HSC format
      const isAustralian = text.includes('Engagement:') || text.includes('Structure:') || text.includes('Analysis:');
      
      if (isAustralian) {
        // Parse Australian categories
        const engagementMatch = text.match(/Engagement:\s*(\d+)\/(\d+)/);
        if (engagementMatch) {
          const engagementFeedback = text.match(/Engagement:\s*\d+\/\d+\s*([^]*?)(?=Structure:|$)/);
          extractedGrades.engagement = {
            score: parseInt(engagementMatch[1]),
            max: parseInt(engagementMatch[2]),
            feedback: engagementFeedback ? engagementFeedback[1].replace(/\n/g, ' ').trim() : ''
          };
        }
        
        const structureMatch = text.match(/Structure:\s*(\d+)\/(\d+)/);
        if (structureMatch) {
          const structureFeedback = text.match(/Structure:\s*\d+\/\d+\s*([^]*?)(?=Analysis:|$)/);
          extractedGrades.structure = {
            score: parseInt(structureMatch[1]),
            max: parseInt(structureMatch[2]),
            feedback: structureFeedback ? structureFeedback[1].replace(/\n/g, ' ').trim() : ''
          };
        }
        
        const analysisMatch = text.match(/Analysis:\s*(\d+)\/(\d+)/);
        if (analysisMatch) {
          const analysisFeedback = text.match(/Analysis:\s*\d+\/\d+\s*([^]*?)(?=Language:|$)/);
          extractedGrades.analysis = {
            score: parseInt(analysisMatch[1]),
            max: parseInt(analysisMatch[2]),
            feedback: analysisFeedback ? analysisFeedback[1].replace(/\n/g, ' ').trim() : ''
          };
        }
        
        const languageMatch = text.match(/Language:\s*(\d+)\/(\d+)/);
        if (languageMatch) {
          const languageFeedback = text.match(/Language:\s*\d+\/\d+\s*([^]*?)(?=Final Mark:|$)/);
          extractedGrades.language = {
            score: parseInt(languageMatch[1]),
            max: parseInt(languageMatch[2]),
            feedback: languageFeedback ? languageFeedback[1].replace(/\n/g, ' ').trim() : ''
          };
        }
      } else {
        // Check if this is VCE Section A/C format (has Knowledge)
        const knowledgeMatch = text.match(/Knowledge:\s*(\d+)\/(\d+)/);
        const isVCESectionAC = !!knowledgeMatch;
        
        // Check if this is VCE Section B format (has Purpose but no Knowledge)
        const hasPurpose = text.includes('Purpose:');
        const isVCESectionB = hasPurpose && !knowledgeMatch;
        
        if (isVCESectionAC) {
          // VCE Section A/C: Knowledge, Coherence, Evidence, Language
          const knowledgeFeedback = text.match(/Knowledge:\s*\d+\/\d+\s*([^]*?)(?=Coherence:|$)/);
          extractedGrades.knowledge = {
            score: parseInt(knowledgeMatch[1]),
            max: parseInt(knowledgeMatch[2]),
            feedback: knowledgeFeedback ? knowledgeFeedback[1].replace(/\n/g, ' ').trim() : ''
          };
          
          const coherenceMatch = text.match(/Coherence:\s*(\d+)\/(\d+)/);
          if (coherenceMatch) {
            const coherenceFeedback = text.match(/Coherence:\s*\d+\/\d+\s*([^]*?)(?=Evidence:|$)/);
            extractedGrades.coherence = {
              score: parseInt(coherenceMatch[1]),
              max: parseInt(coherenceMatch[2]),
              feedback: coherenceFeedback ? coherenceFeedback[1].replace(/\n/g, ' ').trim() : ''
            };
          }
          
          const evidenceMatch = text.match(/Evidence:\s*(\d+)\/(\d+)/);
          if (evidenceMatch) {
            const evidenceFeedback = text.match(/Evidence:\s*\d+\/\d+\s*([^]*?)(?=Language:|$)/);
            extractedGrades.evidence = {
              score: parseInt(evidenceMatch[1]),
              max: parseInt(evidenceMatch[2]),
              feedback: evidenceFeedback ? evidenceFeedback[1].replace(/\n/g, ' ').trim() : ''
            };
          }
          
          const languageMatch = text.match(/Language:\s*(\d+)\/(\d+)/);
          if (languageMatch) {
            const languageFeedback = text.match(/Language:\s*\d+\/\d+\s*([^]*?)(?=Final Mark:|$)/);
            extractedGrades.language = {
              score: parseInt(languageMatch[1]),
              max: parseInt(languageMatch[2]),
              feedback: languageFeedback ? languageFeedback[1].replace(/\n/g, ' ').trim() : ''
            };
          }
        } else if (isVCESectionB) {
          // VCE Section B: Engagement, Purpose, Structure, Language
          const engagementMatch = text.match(/Engagement:\s*(\d+)\/(\d+)/);
          if (engagementMatch) {
            const engagementFeedback = text.match(/Engagement:\s*\d+\/\d+\s*([^]*?)(?=Purpose:|$)/);
            extractedGrades.engagement = {
              score: parseInt(engagementMatch[1]),
              max: parseInt(engagementMatch[2]),
              feedback: engagementFeedback ? engagementFeedback[1].replace(/\n/g, ' ').trim() : ''
            };
          }
          
          const purposeMatch = text.match(/Purpose:\s*(\d+)\/(\d+)/);
          if (purposeMatch) {
            const purposeFeedback = text.match(/Purpose:\s*\d+\/\d+\s*([^]*?)(?=Structure:|$)/);
            extractedGrades.purpose = {
              score: parseInt(purposeMatch[1]),
              max: parseInt(purposeMatch[2]),
              feedback: purposeFeedback ? purposeFeedback[1].replace(/\n/g, ' ').trim() : ''
            };
          }
          
          const structureMatch = text.match(/Structure:\s*(\d+)\/(\d+)/);
          if (structureMatch) {
            const structureFeedback = text.match(/Structure:\s*\d+\/\d+\s*([^]*?)(?=Language:|$)/);
            extractedGrades.structure = {
              score: parseInt(structureMatch[1]),
              max: parseInt(structureMatch[2]),
              feedback: structureFeedback ? structureFeedback[1].replace(/\n/g, ' ').trim() : ''
            };
          }
          
          const languageMatch = text.match(/Language:\s*(\d+)\/(\d+)/);
          if (languageMatch) {
            const languageFeedback = text.match(/Language:\s*\d+\/\d+\s*([^]*?)(?=Final Mark:|$)/);
            extractedGrades.language = {
              score: parseInt(languageMatch[1]),
              max: parseInt(languageMatch[2]),
              feedback: languageFeedback ? languageFeedback[1].replace(/\n/g, ' ').trim() : ''
            };
          }
        } else {
          // Standard English format: Purpose, Coherence, Language Use, Accuracy of Mechanics
          const purposeMatch = text.match(/Purpose:\s*(\d+)\/(\d+)/);
          if (purposeMatch) {
            const purposeFeedback = text.match(/Purpose:\s*\d+\/\d+\s*([^]*?)(?=Coherence:|Language(?:\s+Use)?:|Accuracy of Mechanics:|Final Mark:|$)/);
            extractedGrades.purpose = {
              score: parseInt(purposeMatch[1]),
              max: parseInt(purposeMatch[2]),
              feedback: purposeFeedback ? purposeFeedback[1].replace(/\n/g, ' ').trim() : ''
            };
          }
          
          const coherenceMatch = text.match(/Coherence:\s*(\d+)\/(\d+)/);
          if (coherenceMatch) {
            const coherenceFeedback = text.match(/Coherence:\s*\d+\/\d+\s*([^]*?)(?=Language(?:\s+Use)?:|Accuracy of Mechanics:|Final Mark:|$)/);
            extractedGrades.coherence = {
              score: parseInt(coherenceMatch[1]),
              max: parseInt(coherenceMatch[2]),
              feedback: coherenceFeedback ? coherenceFeedback[1].replace(/\n/g, ' ').trim() : ''
            };
          }
          
          const languageMatch = text.match(/Language(?:\s+Use)?:\s*(\d+)\/(\d+)/);
          if (languageMatch) {
            const languageFeedback = text.match(/Language(?:\s+Use)?:\s*\d+\/\d+\s*([^]*?)(?=Accuracy of Mechanics:|Final Mark:|$)/);
            extractedGrades.language = {
              score: parseInt(languageMatch[1]),
              max: parseInt(languageMatch[2]),
              feedback: languageFeedback ? languageFeedback[1].replace(/\n/g, ' ').trim() : ''
            };
          }
          
          const mechanicsMatch = text.match(/Accuracy of Mechanics:\s*(\d+)\/(\d+)/);
          const mechanicsNAMatch = text.match(/Accuracy of Mechanics:\s*(N\/A)/);
          if (mechanicsMatch) {
            const mechanicsFeedback = text.match(/Accuracy of Mechanics:\s*\d+\/\d+\s*([^]*?)(?=Final Mark:|$)/);
            extractedGrades.mechanics = {
              score: parseInt(mechanicsMatch[1]),
              max: parseInt(mechanicsMatch[2]),
              feedback: mechanicsFeedback ? mechanicsFeedback[1].replace(/\n/g, ' ').trim() : ''
            };
          } else if (mechanicsNAMatch) {
            const mechanicsFeedback = text.match(/Accuracy of Mechanics:\s*N\/A\s*([^]*?)(?=Final Mark:|$)/);
            extractedGrades.mechanics = {
              score: "N/A",
              max: grades.mechanics?.max || 5,
              feedback: mechanicsFeedback ? mechanicsFeedback[1].replace(/\n/g, ' ').trim() : ''
            };
          }
        }
      }
      
      // Parse Final Mark
      const finalMatch = text.match(/Final Mark:\s*(\d+)\/(\d+)\s*\((\d+)%\)/);
      if (finalMatch) {
        extractedGrades.total = {
          score: parseInt(finalMatch[1]),
          max: parseInt(finalMatch[2]),
          percentage: parseInt(finalMatch[3])
        };
      }
      
      setEditedGrades(extractedGrades);
    } else {
      // Use original grades if no custom text exists (already normalized to lowercase)
      setEditedGrades({ ...grades });
    }
    
    setIsStructuredEdit(true);
    handleEditGrade();
  };

  const handleGradeFieldChange = (section, field, value) => {
    setEditedGrades(prev => {
      const updated = {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: field === 'score' || field === 'max' ? parseInt(value) || 0 : value
        }
      };
      
      // Recalculate total whenever score or max values change
      if (field === 'score' || field === 'max') {
        let totalScore = 0;
        let totalMax = 0;
        
        // Sum all components except 'total'
        Object.entries(updated).forEach(([key, componentData]) => {
          if (key !== 'total' && componentData && typeof componentData === 'object') {
            const score = componentData.score;
            const max = componentData.max;
            
            // Handle numeric scores (exclude "N/A" values)
            if (typeof score === 'number' && typeof max === 'number') {
              totalScore += score;
              totalMax += max;
            }
          }
        });
        
        // Update the total
        updated.total = {
          score: totalScore,
          max: totalMax,
          percentage: totalMax > 0 ? Math.round((totalScore / totalMax) * 100) : 0
        };
      }
      
      return updated;
    });
  };

  const handleStructuredGradeSave = async () => {
    try {
      setGradeSaveStatus('saving');
      
      // Calculate total if all scores are available
      const updatedGrades = { ...editedGrades };
      
      // Check if this is Australian HSC format
      const isAustralian = updatedGrades.engagement || updatedGrades.structure || updatedGrades.analysis;
      
      // Check if this is VCE Section A/C format
      const isVCESectionAC = updatedGrades.knowledge && updatedGrades.coherence && updatedGrades.evidence;
      
      // Check if this is VCE Section B format
      const isVCESectionB = updatedGrades.engagement && updatedGrades.purpose && updatedGrades.structure && !updatedGrades.analysis;
      
      if (isAustralian && !isVCESectionB) {
        // Australian HSC calculation (has analysis, not purpose)
        if (updatedGrades.engagement && updatedGrades.structure && updatedGrades.analysis && updatedGrades.language) {
          const totalScore = (updatedGrades.engagement.score || 0) + 
                            (updatedGrades.structure.score || 0) + 
                            (updatedGrades.analysis.score || 0) + 
                            (updatedGrades.language.score || 0);
          
          const totalMax = (updatedGrades.engagement.max || 0) + 
                          (updatedGrades.structure.max || 0) + 
                          (updatedGrades.analysis.max || 0) + 
                          (updatedGrades.language.max || 0);
          
          const percentage = totalMax > 0 ? Math.round((totalScore / totalMax) * 100) : 0;
          
          updatedGrades.total = {
            score: totalScore,
            max: totalMax,
            percentage: percentage
          };
        }
      } else if (isVCESectionB) {
        // VCE Section B calculation (has purpose, not analysis)
        if (updatedGrades.engagement && updatedGrades.purpose && updatedGrades.structure && updatedGrades.language) {
          const totalScore = (updatedGrades.engagement.score || 0) + 
                            (updatedGrades.purpose.score || 0) + 
                            (updatedGrades.structure.score || 0) + 
                            (updatedGrades.language.score || 0);
          
          const totalMax = (updatedGrades.engagement.max || 0) + 
                          (updatedGrades.purpose.max || 0) + 
                          (updatedGrades.structure.max || 0) + 
                          (updatedGrades.language.max || 0);
          
          const percentage = totalMax > 0 ? Math.round((totalScore / totalMax) * 100) : 0;
          
          updatedGrades.total = {
            score: totalScore,
            max: totalMax,
            percentage: percentage
          };
        }
      } else if (isVCESectionAC) {
        // VCE Section A/C calculation
        if (updatedGrades.knowledge && updatedGrades.coherence && updatedGrades.evidence && updatedGrades.language) {
          const totalScore = (updatedGrades.knowledge.score || 0) + 
                            (updatedGrades.coherence.score || 0) + 
                            (updatedGrades.evidence.score || 0) + 
                            (updatedGrades.language.score || 0);
          
          const totalMax = (updatedGrades.knowledge.max || 0) + 
                          (updatedGrades.coherence.max || 0) + 
                          (updatedGrades.evidence.max || 0) + 
                          (updatedGrades.language.max || 0);
          
          const percentage = totalMax > 0 ? Math.round((totalScore / totalMax) * 100) : 0;
          
          updatedGrades.total = {
            score: totalScore,
            max: totalMax,
            percentage: percentage
          };
        }
      } else {
        // Standard English calculation
        if (updatedGrades.purpose && updatedGrades.coherence && updatedGrades.language && updatedGrades.mechanics) {
          const totalScore = (updatedGrades.purpose.score || 0) + 
                            (updatedGrades.coherence.score || 0) + 
                            (updatedGrades.language.score || 0) + 
                            (updatedGrades.mechanics.score === "N/A" ? 0 : (updatedGrades.mechanics.score || 0));
          
          const totalMax = (updatedGrades.purpose.max || 0) + 
                          (updatedGrades.coherence.max || 0) + 
                          (updatedGrades.language.max || 0) + 
                          (updatedGrades.mechanics.max || 0);
          
          const percentage = totalMax > 0 ? Math.round((totalScore / totalMax) * 100) : 0;
          
          updatedGrades.total = {
            score: totalScore,
            max: totalMax,
            percentage: percentage
          };
        }
      }

      // Update the local JSON data immediately for UI feedback
      if (localJsonData) {
        const updatedJsonData = { ...localJsonData };
        // Store with lowercase keys for consistency
        updatedJsonData.grades = updatedGrades;
        // Don't update custom_grade_text here - let backend handle text generation if needed
        setLocalJsonData(updatedJsonData);
      } else {
        // Create new local data with both formats
        const newData = { ...jsonData };
        // Store with lowercase keys for consistency
        newData.grades = updatedGrades;
        setLocalJsonData(newData);
      }

      // Save to backend
      const response = await fetch(`${API_BASE_URL}/api/results/${taskId}/update_grade`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          json_grades: updatedGrades  // Only send the structured data
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || `Failed to save grade: ${response.status} ${response.statusText}`);
      }
      
      const responseData = await response.json();
      
      // Reset editing states
      setIsEditingGrade(false);
      setIsStructuredEdit(false);
      setGradeSaveStatus('success');
      setTimeout(() => setGradeSaveStatus(null), 2000);
      
    } catch (error) {
      console.error('Error saving structured grade:', error);
      setGradeSaveStatus('error');
      alert(`Error saving grade: ${error.message}`);
    }
  };

  // Detect if this is an Australian JSON grade
  const isAustralian = currentJsonData._format === 'australian';

  return (
    <div className="grade-container">
      <div className="grade-header-row">
        <h3 className="grade-title">Grade Calculation</h3>
        {userData?.role === 'teacher' && !isEditingGrade && (
          <div className="grade-edit-buttons">
            <button
              onClick={handleStructuredEdit}
              className="teacher-grade-edit-button"
              title="Edit grade (structured)"
              style={{ marginRight: '8px' }}
            >
              ✏️ Edit Grade
            </button>
          </div>
        )}
      </div>
      
      {/* Add word count warning above the grade */}
      {getWordCountWarning(category, wordCount)}
      
      {isEditingGrade ? (
        <div className="grade-edit-container">
          {isStructuredEdit ? (
            // Structured editing interface
            <div className="structured-grade-edit">
              <h4>Edit Grade Components</h4>
              
              {/* Dynamic rendering of all grade components for editing */}
              {Object.entries(editedGrades)
                .filter(([key]) => key !== 'total') // Exclude total as it's calculated automatically
                .map(([componentKey, componentData]) => {
                  // Capitalize and format the key for display
                  const displayName = componentKey.charAt(0).toUpperCase() + componentKey.slice(1)
                    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                    .replace(/^\s+/, ''); // Remove leading space
                  
                  return (
                    <div key={componentKey} className="grade-section-edit">
                      <h5>{displayName}</h5>
                      <div className="grade-input-row">
                        <label>{displayName}:</label>
                        <input
                          type="number"
                          min="0"
                          max={componentData.max || 5}
                          value={componentData.score === "N/A" ? 0 : (componentData.score || 0)}
                          onChange={(e) => handleGradeFieldChange(componentKey, 'score', e.target.value)}
                          style={{ width: '60px', marginRight: '10px' }}
                        />
                        <span>/ </span>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={componentData.max || 5}
                          onChange={(e) => handleGradeFieldChange(componentKey, 'max', e.target.value)}
                          style={{ width: '60px', marginLeft: '5px' }}
                        />
                      </div>
                      <div className="grade-input-row">
                        <label>Feedback:</label>
                        <textarea
                          value={componentData.feedback || ''}
                          onChange={(e) => handleGradeFieldChange(componentKey, 'feedback', e.target.value)}
                          rows={2}
                          style={{ width: '100%', marginTop: '5px' }}
                        />
                      </div>
                    </div>
                  );
                })
              }

              <div className="grade-edit-buttons">
                <button 
                  onClick={handleStructuredGradeSave}
                  className="grade-edit-button save-button"
                  disabled={gradeSaveStatus === 'saving'}
                >
                  {gradeSaveStatus === 'saving' ? 'Saving...' : 'Save Grade'}
                </button>
                <button 
                  onClick={handleCancelGradeEdit}
                  className="grade-edit-button cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            // Original text editing interface
            <div>
              <textarea
                value={editedGradeText}
                onChange={(e) => setEditedGradeText(e.target.value)}
                className="grade-edit-textarea"
                rows={10}
              />
              <div className="grade-edit-buttons">
                <button 
                  onClick={handleSaveGrade}
                  className="grade-edit-button save-button"
                  disabled={gradeSaveStatus === 'saving'}
                >
                  {gradeSaveStatus === 'saving' ? 'Saving...' : 'Save Grade'}
                </button>
                <button 
                  onClick={handleCancelGradeEdit}
                  className="grade-edit-button cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          
          {gradeSaveStatus && (
            <div className={`grade-save-status grade-save-${gradeSaveStatus}`}>
              {gradeSaveStatus === 'success' && '✓ Saved successfully'}
              {gradeSaveStatus === 'error' && '⚠ Error saving'}
            </div>
          )}
        </div>
      ) : (
        <div className="json-grades-display">
          {/* Display custom teacher-edited grade if it exists, otherwise show structured grades */}
          {currentJsonData.custom_grade_text && currentJsonData.custom_grade_text.includes('[TEACHER]') ? (
            <div className="custom-grade-section">
              <div className="teacher-grade-badge">Teacher Edited</div>
              <div 
                className="custom-grade-text"
                dangerouslySetInnerHTML={{ 
                  __html: currentJsonData.custom_grade_text
                    .replace(/\[TEACHER\]/g, '')
                    .trim()
                    // First normalize all whitespace
                    .replace(/\s+/g, ' ')
                    // Style the main header
                    .replace(/Total Score Calculation\s*/g, '<div class="main-header">Total Score Calculation</div>')
                    // Handle VCE Section A/C grade components
                    .replace(/Knowledge:\s*(\d+\/\d+)\s*([^]*?)(?=Coherence:|$)/g, 
                      '<div class="grade-component"><span class="component-name">Knowledge:</span> <span class="score">$1</span></div><div class="feedback-text">$2</div>')
                    .replace(/Coherence:\s*(\d+\/\d+)\s*([^]*?)(?=Evidence:|Structure:|Analysis:|$)/g, 
                      '<div class="grade-component"><span class="component-name">Coherence:</span> <span class="score">$1</span></div><div class="feedback-text">$2</div>')
                    .replace(/Evidence:\s*(\d+\/\d+)\s*([^]*?)(?=Language:|$)/g, 
                      '<div class="grade-component"><span class="component-name">Evidence:</span> <span class="score">$1</span></div><div class="feedback-text">$2</div>')
                    // Handle Australian HSC grade components
                    .replace(/Engagement:\s*(\d+\/\d+)\s*([^]*?)(?=Structure:|Purpose:|$)/g, 
                      '<div class="grade-component"><span class="component-name">Engagement:</span> <span class="score">$1</span></div><div class="feedback-text">$2</div>')
                    .replace(/Structure:\s*(\d+\/\d+)\s*([^]*?)(?=Analysis:|Language:|$)/g, 
                      '<div class="grade-component"><span class="component-name">Structure:</span> <span class="score">$1</span></div><div class="feedback-text">$2</div>')
                    .replace(/Analysis:\s*(\d+\/\d+)\s*([^]*?)(?=Language:|$)/g, 
                      '<div class="grade-component"><span class="component-name">Analysis:</span> <span class="score">$1</span></div><div class="feedback-text">$2</div>')
                    // Handle Language for both formats
                    .replace(/^Language:\s*(\d+\/\d+)\s*([^]*?)(?=Final Mark:|Purpose:|Coherence:|Language Use:|$)/gm, 
                      '<div class="grade-component"><span class="component-name">Language:</span> <span class="score">$1</span></div><div class="feedback-text">$2</div>')
                    // Handle VCE Section B specific component
                    .replace(/Purpose:\s*(\d+\/\d+)\s*([^]*?)(?=Structure:|Coherence:|$)/g, 
                      '<div class="grade-component"><span class="component-name">Purpose:</span> <span class="score">$1</span></div><div class="feedback-text">$2</div>')
                    // Handle standard English grade components
                    .replace(/Purpose:\s*(\d+\/\d+)\s*([^]*?)(?=Coherence:|$)/g, 
                      '<div class="grade-component"><span class="component-name">Purpose:</span> <span class="score">$1</span></div><div class="feedback-text">$2</div>')
                    .replace(/Coherence:\s*(\d+\/\d+)\s*([^]*?)(?=Language Use:|$)/g, 
                      '<div class="grade-component"><span class="component-name">Coherence:</span> <span class="score">$1</span></div><div class="feedback-text">$2</div>')
                    .replace(/Language Use:\s*(\d+\/\d+)\s*([^]*?)(?=Accuracy of Mechanics:|$)/g, 
                      '<div class="grade-component"><span class="component-name">Language Use:</span> <span class="score">$1</span></div><div class="feedback-text">$2</div>')
                    .replace(/Accuracy of Mechanics:\s*([\d\/]+|N\/A)\s*([^]*?)(?=Final Mark:|$)/g, 
                      '<div class="grade-component"><span class="component-name">Accuracy of Mechanics:</span> <span class="score">$1</span></div><div class="feedback-text">$2</div>')
                    // Handle Final Mark
                    .replace(/Final Mark:\s*(\d+\/\d+\s*\(\d+%\))/g, 
                      '<div class="final-mark-section">Final Mark: <span class="final-score">$1</span></div>')
                }}
              />
            </div>
          ) : (
            <>
              <div className="grade-header-main">Total Score Calculation</div>
              {/* Force VCE Section A/C format if category matches, regardless of grade presence */}
              {(category === 'VCE_English_SectionA' || category === 'VCE_English_SectionC') ? (
                <>
                  {/* VCE Section A/C Format - Always show all 4 components */}
                  <div className="grade-item">
                    <span className="grade-header">Knowledge:</span> {grades.knowledge ? `${grades.knowledge.score}/${grades.knowledge.max}` : 'Not graded'}
                    {grades.knowledge?.feedback && (
                      <div className="grade-feedback">{grades.knowledge.feedback}</div>
                    )}
                  </div>
                  <div className="grade-item">
                    <span className="grade-header">Coherence:</span> {grades.coherence ? `${grades.coherence.score}/${grades.coherence.max}` : 'Not graded'}
                    {grades.coherence?.feedback && (
                      <div className="grade-feedback">{grades.coherence.feedback}</div>
                    )}
                  </div>
                  <div className="grade-item">
                    <span className="grade-header">Evidence:</span> {grades.evidence ? `${grades.evidence.score}/${grades.evidence.max}` : 'Not graded'}
                    {grades.evidence?.feedback && (
                      <div className="grade-feedback">{grades.evidence.feedback}</div>
                    )}
                  </div>
                  <div className="grade-item">
                    <span className="grade-header">Language:</span> {grades.language ? `${grades.language.score}/${grades.language.max}` : 'Not graded'}
                    {grades.language?.feedback && (
                      <div className="grade-feedback">{grades.language.feedback}</div>
                    )}
                  </div>
                </>
              ) : category === 'VCE_English_SectionB' ? (
                <>
                  {/* VCE Section B Format - Always show all 4 components */}
                  <div className="grade-item">
                    <span className="grade-header">Engagement:</span> {grades.engagement ? `${grades.engagement.score}/${grades.engagement.max}` : 'Not graded'}
                    {grades.engagement?.feedback && (
                      <div className="grade-feedback">{grades.engagement.feedback}</div>
                    )}
                  </div>
                  <div className="grade-item">
                    <span className="grade-header">Purpose:</span> {grades.purpose ? `${grades.purpose.score}/${grades.purpose.max}` : 'Not graded'}
                    {grades.purpose?.feedback && (
                      <div className="grade-feedback">{grades.purpose.feedback}</div>
                    )}
                  </div>
                  <div className="grade-item">
                    <span className="grade-header">Structure:</span> {grades.structure ? `${grades.structure.score}/${grades.structure.max}` : 'Not graded'}
                    {grades.structure?.feedback && (
                      <div className="grade-feedback">{grades.structure.feedback}</div>
                    )}
                  </div>
                  <div className="grade-item">
                    <span className="grade-header">Language:</span> {grades.language ? `${grades.language.score}/${grades.language.max}` : 'Not graded'}
                    {grades.language?.feedback && (
                      <div className="grade-feedback">{grades.language.feedback}</div>
                    )}
                  </div>
                </>
              ) : (category === 'Australian_HSC' || category === 'Australian_Textual' || category === 'Australian_Critical' ||
                   category === 'English_HSC_Textual' || category === 'English_HSC_Critical' || category === 'English_HSC_Texts' || 
                   category === 'English_HSC_Craft' || category === 'English_HSC_CraftA' || category === 'English_HSC_CraftB') ? (
                <>
                  {/* Australian HSC Format - Always show all 4 components */}
                  <div className="grade-item">
                    <span className="grade-header">Engagement:</span> {grades.engagement ? `${grades.engagement.score}/${grades.engagement.max}` : 'Not graded'}
                    {grades.engagement?.feedback && (
                      <div className="grade-feedback">{grades.engagement.feedback}</div>
                    )}
                  </div>
                  <div className="grade-item">
                    <span className="grade-header">Structure:</span> {grades.structure ? `${grades.structure.score}/${grades.structure.max}` : 'Not graded'}
                    {grades.structure?.feedback && (
                      <div className="grade-feedback">{grades.structure.feedback}</div>
                    )}
                  </div>
                  <div className="grade-item">
                    <span className="grade-header">Analysis:</span> {grades.analysis ? `${grades.analysis.score}/${grades.analysis.max}` : 'Not graded'}
                    {grades.analysis?.feedback && (
                      <div className="grade-feedback">{grades.analysis.feedback}</div>
                    )}
                  </div>
                  <div className="grade-item">
                    <span className="grade-header">Language:</span> {grades.language ? `${grades.language.score}/${grades.language.max}` : 'Not graded'}
                    {grades.language?.feedback && (
                      <div className="grade-feedback">{grades.language.feedback}</div>
                    )}
                  </div>
                </>
              ) : isAustralian ? (
                <>
                  {/* Legacy Australian format handling */}
                  {grades.engagement && (
                    <div className="grade-item">
                      <span className="grade-header">Engagement:</span> {grades.engagement.score}/{grades.engagement.max}
                      {grades.engagement.feedback && (
                        <div className="grade-feedback">{grades.engagement.feedback}</div>
                      )}
                    </div>
                  )}
                  {grades.structure && (
                    <div className="grade-item">
                      <span className="grade-header">Structure:</span> {grades.structure.score}/{grades.structure.max}
                      {grades.structure.feedback && (
                        <div className="grade-feedback">{grades.structure.feedback}</div>
                      )}
                    </div>
                  )}
                  {grades.analysis && (
                    <div className="grade-item">
                      <span className="grade-header">Analysis:</span> {grades.analysis.score}/{grades.analysis.max}
                      {grades.analysis.feedback && (
                        <div className="grade-feedback">{grades.analysis.feedback}</div>
                      )}
                    </div>
                  )}
                  {grades.language && (
                    <div className="grade-item">
                      <span className="grade-header">Language:</span> {grades.language.score}/{grades.language.max}
                      {grades.language.feedback && (
                        <div className="grade-feedback">{grades.language.feedback}</div>
                      )}
                    </div>
                  )}
                </>
              ) : grades.knowledge ? (
                <>
                  {/* VCE Section A/C Format */}
                  {grades.knowledge && (
                    <div className="grade-item">
                      <span className="grade-header">Knowledge:</span> {grades.knowledge.score}/{grades.knowledge.max}
                      {grades.knowledge.feedback && (
                        <div className="grade-feedback">{grades.knowledge.feedback}</div>
                      )}
                    </div>
                  )}
                  {grades.coherence && (
                    <div className="grade-item">
                      <span className="grade-header">Coherence:</span> {grades.coherence.score}/{grades.coherence.max}
                      {grades.coherence.feedback && (
                        <div className="grade-feedback">{grades.coherence.feedback}</div>
                      )}
                    </div>
                  )}
                  {grades.evidence && (
                    <div className="grade-item">
                      <span className="grade-header">Evidence:</span> {grades.evidence.score}/{grades.evidence.max}
                      {grades.evidence.feedback && (
                        <div className="grade-feedback">{grades.evidence.feedback}</div>
                      )}
                    </div>
                  )}
                  {grades.language && (
                    <div className="grade-item">
                      <span className="grade-header">Language:</span> {grades.language.score}/{grades.language.max}
                      {grades.language.feedback && (
                        <div className="grade-feedback">{grades.language.feedback}</div>
                      )}
                    </div>
                  )}
                </>
              ) : grades.engagement && grades.purpose ? (
                <>
                  {/* VCE Section B Format */}
                  {grades.engagement && (
                    <div className="grade-item">
                      <span className="grade-header">Engagement:</span> {grades.engagement.score}/{grades.engagement.max}
                      {grades.engagement.feedback && (
                        <div className="grade-feedback">{grades.engagement.feedback}</div>
                      )}
                    </div>
                  )}
                  {grades.purpose && (
                    <div className="grade-item">
                      <span className="grade-header">Purpose:</span> {grades.purpose.score}/{grades.purpose.max}
                      {grades.purpose.feedback && (
                        <div className="grade-feedback">{grades.purpose.feedback}</div>
                      )}
                    </div>
                  )}
                  {grades.structure && (
                    <div className="grade-item">
                      <span className="grade-header">Structure:</span> {grades.structure.score}/{grades.structure.max}
                      {grades.structure.feedback && (
                        <div className="grade-feedback">{grades.structure.feedback}</div>
                      )}
                    </div>
                  )}
                  {grades.language && (
                    <div className="grade-item">
                      <span className="grade-header">Language:</span> {grades.language.score}/{grades.language.max}
                      {grades.language.feedback && (
                        <div className="grade-feedback">{grades.language.feedback}</div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Dynamic rendering of all available grade categories (excluding 'total') */}
                  {Object.entries(grades)
                    .filter(([key]) => key !== 'total') // Exclude total as it's handled separately
                    .sort(([keyA, gradeA], [keyB, gradeB]) => {
                      // Define PCLM order for English categories
                      const pclmOrder = {
                        'purpose': 1,
                        'coherence': 2,
                        'language': 3,
                        'language_use': 3,
                        'mechanics': 4,
                        'accuracy_of_mechanics': 4
                      };
                      
                      // Check if both keys are in PCLM order
                      const orderA = pclmOrder[keyA.toLowerCase()];
                      const orderB = pclmOrder[keyB.toLowerCase()];
                      
                      if (orderA && orderB) {
                        // Both are PCLM categories, sort by defined order
                        return orderA - orderB;
                      } else if (orderA || orderB) {
                        // One is PCLM, prioritize PCLM categories
                        return orderA ? -1 : 1;
                      } else {
                        // Neither are PCLM, sort by max marks (descending)
                        return (gradeB.max || 0) - (gradeA.max || 0);
                      }
                    })
                    .map(([key, gradeData]) => {
                      // Capitalize and format the key for display
                      const displayName = key.charAt(0).toUpperCase() + key.slice(1)
                        .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                        .replace(/^\s+/, ''); // Remove leading space
                      
                      return (
                        <div key={key} className="grade-item">
                          <span className="grade-header">{displayName}:</span> {gradeData.score}/{gradeData.max}
                          {gradeData.feedback && (
                            <div className="grade-feedback">{gradeData.feedback}</div>
                          )}
                        </div>
                      );
                    })
                  }
                </>
              )}
              {grades.total && (
                <div className="grade-item">
                  <span className="grade-header-final">Final Mark:</span> {grades.total.score}/{grades.total.max} ({grades.total.percentage}%)
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      <div className="grade-caveat">
        <p>
          <strong>Estimated Grade:</strong> This is an estimated grade. Pulc is often a harsh marker and sometimes
          makes mistakes. Always rely on your teacher's grade and feedback first. For some FAQs
          about estimated grades, see <button onClick={toggleFAQ} className="faq-link">here</button>.
        </p>
      </div>
    </div>
  );
}

export default JsonGrades; 